'use client';

import { useEffect } from 'react';

/**
 * Mobile-specific optimizations applied at the app root level.
 * 
 * Handles:
 * - iOS zoom prevention on input focus (font-size 16px enforcement)
 * - Smooth scrolling behavior
 * - Touch action optimizations
 * - Passive event listeners
 * - Back/forward cache optimization
 * - Reduce motion preference
 */
export function MobileOptimizations() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // 1. Prevent iOS zoom on input focus by setting font-size ≥ 16px
    // (already handled in Tailwind, but enforce dynamically for edge cases)
    const inputs = document.querySelectorAll('input, select, textarea');
    inputs.forEach((input) => {
      const el = input as HTMLElement;
      const fontSize = window.getComputedStyle(el).fontSize;
      if (parseFloat(fontSize) < 16) {
        el.style.fontSize = '16px';
      }
    });

    // 2. Optimize touch scrolling
    document.documentElement.style.setProperty('-webkit-overflow-scrolling', 'touch');

    // 3. Preload critical above-fold content on mobile
    if (window.innerWidth < 768) {
      // Prefetch key mobile navigation paths
      const mobileLinks = ['/medications', '/how-it-works', '/support'];
      mobileLinks.forEach((path) => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = path;
        document.head.appendChild(link);
      });
    }

    // 4. Handle back/forward cache (bfcache)
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        // Page was restored from bfcache - refresh dynamic content
        document.dispatchEvent(new CustomEvent('bfcache-restore'));
      }
    });

    // 5. Reduce motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (prefersReducedMotion.matches) {
      document.documentElement.classList.add('reduce-motion');
    }
  }, []);

  return null;
}
